import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Award, Globe2, FileText, Check, GraduationCap, MessageSquareQuote } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Spinner, SectionLabel, Pill } from "../components/Primitives";

export default function LearnerProfile() {
  const { learnerId } = useParams();
  const { user, login } = useAuth();
  const [learner, setLearner] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    Promise.all([
      api.get("/learners"),
      api.get("/assignments"),
      api.get("/coursework/my-feedback").catch(() => ({ data: [] })),
      api.get("/certificates").catch(() => ({ data: [] })),
    ]).then(([l, a, f, c]) => {
      setLearner(l.data.find((x) => x.id === learnerId) || null);
      setAssignments(a.data.filter((x) => x.learner_id === learnerId));
      setFeedback(f.data);
      setCerts(c.data);
    }).finally(() => setLoading(false));
  }, [user, learnerId]);

  if (!user) return (
    <div className="mx-auto max-w-xl px-6 py-32 text-center">
      <p className="text-muted">Sign in to view learner profiles.</p>
      <button onClick={login} data-testid="learner-signin-btn" className="mt-4 border border-gold px-4 py-2 text-gold hover:bg-gold hover:text-ink">Sign In</button>
    </div>
  );
  if (loading) return <div className="min-h-[60vh]"><Spinner /></div>;
  if (!learner) return (
    <div className="mx-auto max-w-xl px-6 py-32 text-center">
      <p className="text-muted" data-testid="learner-not-found">Learner not found.</p>
      <Link to="/dashboard" className="mt-4 inline-block text-gold hover:underline">← Back to dashboard</Link>
    </div>
  );

  const slugs = new Set(assignments.filter((a) => a.item_type === "coursework").map((a) => a.slug));
  const latestBySlug = {};
  for (const f of feedback) if (!(f.slug in latestBySlug)) latestBySlug[f.slug] = f;
  const grades = [...slugs].map((s) => latestBySlug[s]).filter((g) => g && g.score != null);
  const avg = grades.length ? Math.round(grades.reduce((s, g) => s + g.score, 0) / grades.length) : null;
  const learnerCerts = certs.filter((c) => slugs.has(c.coursework_slug));
  const done = assignments.filter((a) => a.status === "completed").length;

  return (
    <div data-testid="learner-profile-page" className="mx-auto max-w-4xl px-6 py-12 md:px-12">
      <Link to="/dashboard" className="mb-6 flex items-center gap-2 text-sm text-muted hover:text-gold">
        <ArrowLeft className="h-4 w-4" /> Dashboard
      </Link>
      <SectionLabel>Learner Profile</SectionLabel>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-serif text-4xl font-semibold text-bone lg:text-5xl">{learner.name}</h1>
          {learner.level && <p className="mt-2 flex items-center gap-2 text-muted"><GraduationCap className="h-4 w-4" /> {learner.level}</p>}
        </div>
        <div className="flex gap-3">
          <StatBox label="Completed" value={`${done}/${assignments.length}`} testid="lp-completed" />
          {avg != null && <StatBox label="Avg Score" value={`${avg}%`} testid="lp-avg" />}
          <StatBox label="Certificates" value={learnerCerts.length} testid="lp-certs" />
        </div>
      </div>

      <h2 className="mb-4 mt-12 font-serif text-2xl text-bone">Assigned Work</h2>
      {assignments.length === 0 ? (
        <p className="border border-dashed border-edge p-8 text-center text-muted" data-testid="lp-no-assignments">
          Nothing assigned yet — assign Globe explorations or published assignments.
        </p>
      ) : (
        <div className="space-y-2" data-testid="lp-assignments">
          {assignments.map((a) => (
            <Link key={a.id} to={a.item_type === "coursework" ? `/assignments/${a.slug}` : "/globe"}
              data-testid={`lp-assignment-${a.id}`}
              className="group flex items-center gap-3 border border-edge bg-ash p-4 transition-colors hover:border-gold/50">
              {a.item_type === "coursework" ? <FileText className="h-4 w-4 flex-shrink-0 text-gold" /> : <Globe2 className="h-4 w-4 flex-shrink-0 text-gold" />}
              <span className={`flex-1 ${a.status === "completed" ? "text-muted line-through" : "text-bone"} group-hover:text-gold`}>{a.title}</span>
              {a.status === "completed"
                ? <span className="flex items-center gap-1 border border-gold/50 bg-gold/10 px-2 py-0.5 font-mono text-xs text-gold"><Check className="h-3 w-3" /> Done</span>
                : <span className="border border-edge px-2 py-0.5 font-mono text-xs text-muted">Assigned</span>}
            </Link>
          ))}
        </div>
      )}

      <h2 className="mb-4 mt-12 font-serif text-2xl text-bone">Grades</h2>
      {grades.length === 0 ? (
        <p className="border border-dashed border-edge p-8 text-center text-muted" data-testid="lp-no-grades">No graded coursework yet.</p>
      ) : (
        <div className="space-y-3" data-testid="lp-grades">
          {grades.map((g) => (
            <div key={g.submission_id} className="border border-edge bg-ash p-4" data-testid={`lp-grade-${g.slug}`}>
              <div className="flex items-center justify-between gap-3">
                <Link to={`/assignments/${g.slug}`} className="text-bone hover:text-gold">{g.title}</Link>
                <span className="border border-gold/50 bg-gold/10 px-2 py-0.5 font-mono text-sm text-gold">{g.score}%</span>
              </div>
              {g.feedback && (
                <p className="mt-2 flex items-start gap-2 text-sm text-muted">
                  <MessageSquareQuote className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-gold" /> “{g.feedback}”
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      <h2 className="mb-4 mt-12 font-serif text-2xl text-bone">Certificates</h2>
      {learnerCerts.length === 0 ? (
        <p className="border border-dashed border-edge p-8 text-center text-muted" data-testid="lp-no-certs">
          No certificates yet — a grade of 80% or higher earns one.
        </p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2" data-testid="lp-certificates">
          {learnerCerts.map((c) => (
            <Link key={c.submission_id} to={`/assignments/${c.coursework_slug}`} data-testid={`lp-cert-${c.submission_id}`}
              className="group border border-gold/40 bg-ash p-5 transition-colors hover:border-gold">
              <div className="flex items-start justify-between gap-2">
                <Award className="h-6 w-6 text-gold" />
                <span className="border border-gold/50 bg-gold/10 px-2 py-0.5 font-mono text-xs text-gold">{c.score}%</span>
              </div>
              <p className="mt-3 font-serif text-lg text-bone group-hover:text-gold">{c.title}</p>
              <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.12em] text-muted">
                {c.awarded_at ? new Date(c.awarded_at).toLocaleDateString() : ""} · {c.awarded_by}
              </p>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function StatBox({ label, value, testid }) {
  return (
    <div className="border border-edge bg-ash px-5 py-3 text-center" data-testid={testid}>
      <p className="font-serif text-2xl text-gold">{value}</p>
      <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted">{label}</p>
    </div>
  );
}
